import { useEffect, useState } from "react";

const LOCATION_CACHE = new Map<string, string>();

const DEFAULT_FALLBACK = "Location not provided";

const normalizeZip = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const digitsOnly = value.replace(/[^0-9]/g, "");
    if (digitsOnly.length === 5) return digitsOnly;
    if (digitsOnly.length === 9) return digitsOnly.slice(0, 5);
    return null;
};

const withZip = (label: string, zip: string | null): string => {
    if (!zip) return label;
    return `${label} (${zip})`;
};

const getFallbackLabel = (raw: string | null | undefined, fallback?: string): string => {
    const defaultLabel = fallback ?? DEFAULT_FALLBACK;
    if (!raw) return defaultLabel;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : defaultLabel;
};

export function useZipLocation(rawLocation: string | null | undefined, fallbackLabel?: string): string {
    const [computedLocation, setComputedLocation] = useState<string>(
        getFallbackLabel(rawLocation, fallbackLabel)
    );

    useEffect(() => {
        const fallback = getFallbackLabel(rawLocation, fallbackLabel);
        setComputedLocation(fallback);

        const zip = normalizeZip(rawLocation);
        if (!zip) {
            return;
        }

        if (LOCATION_CACHE.has(zip)) {
            setComputedLocation(LOCATION_CACHE.get(zip)!);
            return;
        }

        let isActive = true;
        const controller = new AbortController();

        const fetchLocation = async () => {
            try {
                const response = await fetch(`https://api.zippopotam.us/us/${zip}`, {
                    signal: controller.signal,
                });

                if (!response.ok) {
                    const fallbackWithZip = withZip(fallback, zip);
                    LOCATION_CACHE.set(zip, fallbackWithZip);
                    if (isActive) setComputedLocation(fallbackWithZip);
                    return;
                }

                const data: any = await response.json();
                if (!isActive) return;

                const place = Array.isArray(data?.places) && data.places.length > 0 ? data.places[0] : null;
                const city: string | undefined = place?.["place name"] ?? place?.place;
                const state: string | undefined = place?.["state abbreviation"] ?? place?.state;

                if (city && state) {
                    const formatted = `${city}, ${state} (${zip})`;
                    LOCATION_CACHE.set(zip, formatted);
                    setComputedLocation(formatted);
                } else {
                    const fallbackWithZip = withZip(fallback, zip);
                    LOCATION_CACHE.set(zip, fallbackWithZip);
                    setComputedLocation(fallbackWithZip);
                }
            } catch (error) {
                if (controller.signal.aborted || !isActive) return;
                const fallbackWithZip = withZip(fallback, zip);
                LOCATION_CACHE.set(zip, fallbackWithZip);
                setComputedLocation(fallbackWithZip);
            }
        };

        void fetchLocation();

        return () => {
            isActive = false;
            controller.abort();
        };
    }, [rawLocation, fallbackLabel]);

    return computedLocation;
}

export function getZipFromLocation(rawLocation: string | null | undefined): string | null {
    return normalizeZip(rawLocation);
}
